import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TldService } from '../../core/services/tld.service';
import { APIErrorModel, BaseResponse } from '../../shared/models/base-response.model';
import { firstValueFrom } from 'rxjs';
import { checkEmailStatusRequestModel, checkEmailStatusResponseModel, GetAreaOfInterestResponseModel, GetImageUploadLinkRequestModel, GetImageUploadLinkResponseModel, UpdateProfileRequestModel, UpdateProfileRespModel, UserEventStatusRespModel } from './profile.model';
import { HttpResult } from '../../core/services/base-http.service';
import { ProfileApiService } from './profile-api.service';
import { StorageService } from '../../core/services/storage.service';
import { UserService } from '../../core/services/user.service';

import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ProfileBllService {
  constructor(
    private api: ProfileApiService,
    private storageService: StorageService,
    private userService : UserService,
    private router: Router,
    @Inject(DOCUMENT) private document : Document
  ) { }

  /**
 * Email format validator
 * @returns Validator function
 */
  emailFormatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {

      // If contains space anywhere, invalid
      if (/\s/.test(control.value)) {
        return { invalidEmail: true };
      }

      const email = (control.value || '').trim();
      if (!email) return null;

      // 1. Split into local and domain parts
      const atIndex = email.indexOf('@');
      if (atIndex <= 0) return { invalidEmail: true }; // No '@' or starts with '@'
      if (email.indexOf('@', atIndex + 1) !== -1) return { invalidEmail: true }; // More than one '@'

      const localPart = email.substring(0, atIndex);
      const domainPart = email.substring(atIndex + 1);

      // 2. Local part checks
      if (!/^[A-Za-z0-9]/.test(localPart[0])) return { invalidEmail: true }; // must start with letter/number
      if (!/^[A-Za-z0-9._-]+$/.test(localPart)) return { invalidEmail: true }; // only allowed chars
      if (/([._-]{2,})/.test(localPart)) return { invalidEmail: true }; // no consecutive special chars
      if (!/[A-Za-z0-9]$/.test(localPart)) return { invalidEmail: true }; // must end with alphanumeric

      // 3. Domain part checks
      if (!/^[A-Za-z0-9]/.test(domainPart[0])) return { invalidEmail: true }; // must start with letter/number
      const domainSections = domainPart.split('.');
      if (domainSections.length < 2) return { invalidEmail: true }; // must have at least one '.'

      // Check each domain label except TLD
      for (let i = 0; i < domainSections.length - 1; i++) {
        const label = domainSections[i];
        if (!label || !/^[A-Za-z0-9]+$/.test(label)) return { invalidEmail: true };
      }

      // TLD check
      const tld = domainSections[domainSections.length - 1];
      if (tld.length < 2 || !/^[A-Za-z]+$/.test(tld)) return { invalidEmail: true };

      return null; // valid
    };
  }

  /**
   * Custom validator to check if the name contains only valid characters
   * @returns Validator function
   */
  nameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Get the input value or default to empty string
      const value = control.value || '';

      // List of allowed special characters in names
      const allowedSpecialChars = ['.', '-', '\'', '’'];

      // Function to check if a character is allowed
      const isAllowedChar = (char: string) =>
        /^[\p{L}\p{M}\d]$/u.test(char) || char === ' ' || allowedSpecialChars.includes(char) || char === '\u200D';

      // Check for invalid characters
      const hasInvalidChars = [...value].some(char => !isAllowedChar(char));

      // Check for consecutive special characters or spaces
      const hasConsecutiveSpecials = /([.\-’'])\1+/.test(value);

      // Check for consecutive spaces
      const hasConsecutiveSpaces = /\s{2,}/.test(value); // detects 2 or more spaces in a row
      // Check if the name contains any numbers
      const hasNumbers = /\d/.test(value);

      // Return validation errors based on checks
      if (hasInvalidChars) {
        return { invalidName: true };
      }
      if (hasConsecutiveSpecials) {
        return { consecutiveSpecials: true };
      }
      if (hasConsecutiveSpaces) {
        return { invalidName: true };
      }
      if (hasNumbers) {
        return { invalidName: true };
      }

      // If all checks pass, the name is valid
      return null;
    };
  }

  /**
 * Custom validator to ensure the first character is a letter
 * @returns Validator function
 */
  startsWithLetterValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Get the input value as a string
      let value = control.value as string;
      if (!value) {
        // If value is empty, let other validators handle it
        return null;
      }

      // Trim leading spaces
      const trimmedValue = value.replace(/^\s+/, '');
      if (trimmedValue !== value) {
        // Update the control value without emitting another event
        control.setValue(trimmedValue, { emitEvent: false });
        value = trimmedValue;
      }

      // Get the first character
      const firstChar = value.charAt(0);

      // Check if the first character is a letter (including Unicode letters)
      const isValid = /^[\p{L}\p{M}]$/u.test(firstChar);

      // Return null if valid, or a validation error if not
      return isValid ? null : { startsWithLetter: true };
    };
  }

  /**
 * Custom validator to check if the email has a valid TLD
 * @param tldService Service to validate TLDs
 * @returns Validator function
 */
  emailTldValidator(tldService: TldService): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Get the input value or default to empty string
      const value: string = control.value?.trim() || '';

      // Check for empty value
      if (!value) return null; // Let 'required' handle empty

      // Find the last occurrence of '@'
      const atIndex = value.lastIndexOf('@');

      // If '@' is missing or at the end, invalid
      if (atIndex === -1 || atIndex === value.length - 1) {
        return null;
      }

      // Extract domain part after '@'
      const domainPart = value.substring(atIndex + 1);

      // Get the TLD (last segment after the last dot)
      const tld = domainPart.split('.').pop()?.toLowerCase();


      // If TLD is missing or invalid, return error
      if (!tld || !tldService.isValidTld(tld)) {
        return { invalidTld: true };
      }

      // If all checks pass, the TLD is valid
      return null;
    };
  }

  /**
   *
   * @returns Promise resolving to user event status or rejecting with APIErrorModel
   */
  async submitGetUserEventStatus() {
    // Await the API response for getting the user event status
    const result: HttpResult<BaseResponse<UserEventStatusRespModel>> =
      await firstValueFrom(this.api.getUserEventStatus());

    // Destructure status and body from the result
    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      return body;
    } else {
      // If not success, throw error with details
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Submits a request to update the user profile
   * @param payload Profile update request payload
   * @returns Promise resolving to update profile response or rejecting with APIErrorModel
   */
  async submitUpdateProfile(payload: UpdateProfileRequestModel) {
    // Await the API response for updating the profile
    const result: HttpResult<BaseResponse<UpdateProfileRespModel>> =
      await firstValueFrom(this.api.updateBasicProfile(payload));

    // Destructure status and body from the result
    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      this.storageService.saveUserName(payload.first_name + " " + payload.last_name);
      this.userService.updateUser({
        name: payload.first_name + " " + payload.last_name,

      });

      return body;
    } else {
      // If not success, throw error with details
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Submits a request to get a pre-signed URL for image upload
   * @param title Title for the image to be uploaded
   * @returns Promise resolving to upload image link response or rejecting with APIErrorModel
   */
  async submitGetUploadImageLink(title: string) {

    // Prepare the request payload with the image title
    const payload: GetImageUploadLinkRequestModel = {
      title: title
    };

    // Await the API response for getting the upload image link
    const result: HttpResult<BaseResponse<GetImageUploadLinkResponseModel>> =
      await firstValueFrom(this.api.getUploadImageLink(payload));

    // Destructure status and body from the result
    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      this.storageService.saveUserImgUrl(body?.data?.media_url);
      this.userService.updateUser({
       profileImg: body?.data?.media_url

      });
      return body;
    } else {
      // If not success, throw error with details
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
  * Upload image to S3 using API layer and handle response
  */
  async uploadImageToS3(
    mediaUrl: string,
    fields: Record<string, string>,
    file: File
  ) {
    const result: HttpResult<any> = await firstValueFrom(
      this.api.uploadLogoToS3Api(mediaUrl, fields, file)
    );

    const { status, body } = result;
    return status;
  }

  /**
   * Submits a request to check if an email is already registered or usable
   * @param email Email to check
   * @returns Promise resolving to email status response or rejecting with APIErrorModel
   */
  async submitCheckEmailStatus(email: string) {
    // Prepare the request payload with the email to check
    const payload: checkEmailStatusRequestModel = {
      email: email
    };

    // Await the API response for the email status check
    const result: HttpResult<BaseResponse<checkEmailStatusResponseModel>> =
      await firstValueFrom(this.api.checkEmailStatus(payload));

    // Destructure status and body from the result
    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      return body;
    } else {
      // If not success, throw error with details
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Retrieves the area of interest data from the API.
   *
   * Sends an HTTP request to fetch area of interest information and returns a unified response
   * containing the status and body. If the request is successful (status 200), the response body is returned.
   * Otherwise, an error is thrown with the status, message, and data from the response.
   *
   * @returns {Promise<BaseResponse<GetAreaOfInterestResponseModel>>} A promise that resolves with the area of interest data.
   * @throws {APIErrorModel} Throws an error if the request fails, containing the status, message, and data.
   */
  async getAreaOfInterestData(): Promise<BaseResponse<GetAreaOfInterestResponseModel>> {
    // Send HTTP request and wait for the unified response containing status and body
    const result: HttpResult<BaseResponse<GetAreaOfInterestResponseModel>> =
      await firstValueFrom(this.api.getAreaOfInterest());

    const { status, body } = result;

    if (status === 200) {
      // Return data if status is OK
      return body;
    } else {
      // Throw error with status and message if request fails
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Updates the user's area of interest by sending the provided areaOfInterestIds to the API.
   *
   * @param areaOfInterestIds - A comma-separated string of area of interest IDs to update.
   * @returns A promise that resolves to a `BaseResponse<any>` if the update is successful.
   * @throws {APIErrorModel} If the API request fails, throws an error containing the status, message, and data.
   */
  async updateAreaOfInterest(areaOfInterestIds: string): Promise<BaseResponse<any>> {
    // Send HTTP request and wait for the unified response containing status and body
    const result: HttpResult<BaseResponse<any>> = await firstValueFrom(
      this.api.updateAreaOfInterest(areaOfInterestIds)
    );

    const { status, body } = result;

    if (status === 200) {
      // Return data if status is OK
      return body;
    } else {
      // Throw error with status and message if request fails
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }
  getSelectedLanguage(): string {
    const match = this.document.cookie.match(/(^|;) ?googtrans=([^;]*)/);
    if (match && match[2]) {
      const parts = match[2].split('/');
      return parts[2] || 'en'; // default fallback
    }
    return 'en';
  }

  /**
    * Retrieves the login redirect link from storage.
    *
    * @returns {string | null} The login redirect link if it exists, otherwise null.
    */
  getLinkLoginRedirect(): string | null {
    return this.storageService.getLinkLoginRedirect();
  }

  /**
    * Retrieves the query parameters for the login redirect link.
    *
    * This method interacts with the storage service to obtain the necessary
    * query parameters that should be included in the login redirect URL.
    *
    * @returns {QueryParamsType} The query parameters for the login redirect.
    */
  getLinkLoginRedirectQueryParams() {
    return this.storageService.getLinkLoginRedirectQueryParams();
  }

  /**
    * Clears the link login redirect data from storage.
    * This method is typically used to reset the login state
    * when the user has successfully logged in or when the
    * login process is aborted.
    */
  clearLinkLoginRedirectData() {
    this.storageService.clearLinkLoginRedirectData();
  }

  redirectToLinkLogin() {
    const redirectUrl = this.getLinkLoginRedirect();
    const queryParams = this.getLinkLoginRedirectQueryParams();

    if (redirectUrl) {
      switch (redirectUrl) {
        case "invite_details":
          this.router.navigate(["exh-invite/details"], {
            queryParams: queryParams,
          });
          break;
          default:
            console.warn("Unknown redirect value:",redirectUrl);
            this.router.navigate(["home"]);
        }
    }
  }


}
